import { play } from 'cuelume'

import { buildCondition } from '@/features/order/order-form/lib/build-trigger-condition'
import {
  buildTriggerFirePayload,
  type TriggerFirePayloadMode,
} from '@/features/order/order-form/lib/build-trigger-fire-payload'
import { isExitActive } from '@/features/order/order-form/lib/exit-active'
import { buildExitBracketLegs } from '@/features/order/order-form/lib/exit-bracket'
import { resolveConditionTree } from '@/features/order/order-form/lib/resolve-condition-tree'
import { buildRelativePrice } from '@/features/order/order-form/lib/submitters/shared'
import type { SingleHedgeSubmission } from '@/features/order/order-form/lib/submitters/single-hedge'
import type {
  SubmissionRow,
  SubmitterContext,
} from '@/features/order/order-form/lib/submitters/types'
import { validateConditionTree } from '@/features/order/order-form/lib/trigger-condition-schema'
import { trackOrderItems } from '@/lib/analytics-order'
import { toast } from '@/lib/toast'
import type { useConfirmExecutionPlan } from '@/service/execution-plan.hook'
import type { OrderFormCollapsibleConfig } from '@/state/order'
import { Denomination } from '@/types/generated/common/trading'
import type { Instrument } from '@/types/generated/common/types'
import { DraftExecutionPlan, TriggeredItem } from '@/types/generated/public_api'
import { type Trigger } from '@/types/generated/triggers'

export type SubmitTriggerAlgoDeps = {
  instruments: Instrument[]
  isMarketOrder: boolean
  isTwapMode: boolean
  isLimitChaseMode: boolean
  supportsTriggerOrders: boolean
  getCollapsibleConfig: () => OrderFormCollapsibleConfig
  confirmPlan: ReturnType<typeof useConfirmExecutionPlan>['mutateAsync']
  // Set on the Hedge tab: the trigger fires this hedge and the rows are not read.
  buildSingleHedge?: () => SingleHedgeSubmission | null
}

// ── Trigger algo submit ──────────────────────────────────────────────────
export async function submitTriggerAlgoOrder(ctx: SubmitterContext, deps: SubmitTriggerAlgoDeps) {
  const { t, form } = ctx
  const {
    instruments,
    isMarketOrder,
    isTwapMode,
    isLimitChaseMode,
    supportsTriggerOrders,
    getCollapsibleConfig,
    confirmPlan,
    buildSingleHedge,
  } = deps
  let rows: SubmissionRow[] = []
  if (!buildSingleHedge) {
    if (!ctx.validateSubmissionRows()) return
    rows = ctx.getSubmissionRows()
    if (rows.length === 0) {
      toast.error(t('selectAccountFirst'), { position: 'bottom-right' })
      return
    }
  }

  const collapsibleConfig = getCollapsibleConfig()
  const ec = collapsibleConfig

  // The structured condition tree is authoritative; fall back to the legacy
  // flat config so triggers saved before the tree model still open.
  const root = resolveConditionTree(ec)
  if (!root) {
    toast.error(t('configureTriggerCondition'), { position: 'bottom-right' })
    return
  }

  const validationError = validateConditionTree(root)
  if (validationError) {
    toast.error(validationError, { position: 'bottom-right' })
    return
  }

  // A single schedule trigger already fires at a fixed time, so a separate
  // expiry is meaningless and not shown in the UI — never attach one here.
  const scheduleOnly = root.kind === 'schedule'
  const expiryMin = scheduleOnly ? '' : ec.entryConditionExpiryMinutes || ''
  const expiryMs =
    expiryMin && Number(expiryMin) > 0 ? String(Date.now() + Number(expiryMin) * 60_000) : undefined

  if (buildSingleHedge) {
    const hedge = buildSingleHedge()
    if (!hedge) return
    await submitTriggeredSingleHedge(ctx, confirmPlan, hedge, root, expiryMs)
    return
  }

  const rawValues = ctx.getRawValues()
  ctx.setIsSubmitting(true)

  const triggerMode: TriggerFirePayloadMode = isTwapMode
    ? 'twap'
    : isLimitChaseMode
      ? 'limitChase'
      : 'simple'

  // Simple-mode (limit/market) extras must mirror what submitLimitMarketOrder
  // sends, otherwise the fired order diverges from the un-triggered submit.
  let simpleOrderExtras: Parameters<typeof buildTriggerFirePayload>[0]['simpleOrderExtras']
  if (triggerMode === 'simple') {
    const priceAnchor = form.getValues('priceAnchor') ?? 'price'
    const priceOffsetUnit = form.getValues('priceOffsetUnit') ?? 'quote_value'
    const isRelativeLimit = !isMarketOrder && priceAnchor !== 'price'
    simpleOrderExtras = {
      relativePrice: isRelativeLimit
        ? buildRelativePrice(priceAnchor, priceOffsetUnit, rawValues.price.toString())
        : undefined,
      reduceOnly: form.getValues('reduceOnly') || undefined,
      postOnly: form.getValues('postOnly') || undefined,
      tif: form.getValues('timeInForce'),
    }
  }

  const rowsByAccount = new Map<string, SubmissionRow[]>()
  for (const row of rows) {
    const accountRows = rowsByAccount.get(row.account.accountId) ?? []
    accountRows.push(row)
    rowsByAccount.set(row.account.accountId, accountRows)
  }
  const triggeredItems: TriggeredItem[] = []
  try {
    for (const accountRows of rowsByAccount.values()) {
      const account = accountRows[0].account
      const exchange = accountRows[0].exchange
      const items = accountRows.map((row) => row.item)
      const firePayload = buildTriggerFirePayload({
        items,
        rawValues: { ...rawValues, items: accountRows.map((row) => row.rawItem) },
        accountId: account.accountId,
        mode: triggerMode,
        isMarketOrder,
        instruments,
        primaryExchange: exchange,
        tradeVenue: account.tradeVenue,
        simpleOrderExtras,
      })

      const entryCommands = firePayload.executionCommands?.commands
      if (
        triggerMode === 'simple' &&
        items.length === 1 &&
        entryCommands?.length === 1 &&
        entryCommands[0].new &&
        isExitActive(collapsibleConfig) &&
        supportsTriggerOrders
      ) {
        const exitLegs = buildExitBracketLegs(
          entryCommands[0].new,
          items[0].side as 'buy' | 'sell',
          {
            takeProfit: collapsibleConfig?.priceAbove,
            stopLoss: collapsibleConfig?.priceBelow,
          }
        )
        for (const leg of exitLegs) {
          entryCommands.push({ accountId: account.accountId, new: leg })
        }
      }

      const condition = buildCondition(root, {
        exchange,
        tradeVenue: account.tradeVenue,
        defaultSymbol: items[0]?.symbol || '',
        defaultAccountId: account.accountId,
      })
      const trigger: Trigger = { condition, expiryMs }
      triggeredItems.push({ accountId: account.accountId, firePayload, trigger })
    }
  } catch (err) {
    toast.error(err instanceof Error ? err.message : t('failedBuildTriggerPayload'), {
      position: 'bottom-right',
    })
    ctx.setIsSubmitting(false)
    return
  }

  const plan: DraftExecutionPlan = {
    accountId: rows[0].account.accountId,
    executionBatch: undefined,
    warnings: [],
    triggeredItems,
    refusals: [],
  }

  try {
    await confirmPlan(plan)
    trackOrderItems(
      'trigger',
      rows.map(({ item, rawItem, account }) => ({
        accountId: account.accountId,
        symbol: item.symbol,
        quantity: String(rawItem.amount ?? 0),
        denomination: Denomination.DENOMINATION_UNSPECIFIED,
      })),
      ctx.getAnalyticsAccounts(),
      'order-form'
    )
    // No success toast — the WS algoLifecycle STARTED notification already
    // shows one (notification-manager.tsx), and firing both looked like a duplicate.
    ctx.resetFormAfterOrder()
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('triggerOrderFailed'), {
      position: 'bottom-right',
    })
    play('error')
    ctx.setIsSubmitting(false)
  }
}

// One triggered item whose fire payload starts the hedge. The rows are left alone, as for the
// untriggered hedge: it never read their sizes or prices.
async function submitTriggeredSingleHedge(
  ctx: SubmitterContext,
  confirmPlan: SubmitTriggerAlgoDeps['confirmPlan'],
  hedge: SingleHedgeSubmission,
  root: NonNullable<ReturnType<typeof resolveConditionTree>>,
  expiryMs: string | undefined
) {
  const { t } = ctx
  const accountId = hedge.account.accountId
  ctx.setIsSubmitting(true)
  try {
    const firePayload = buildTriggerFirePayload({
      items: [],
      rawValues: ctx.getRawValues(),
      accountId,
      mode: 'singleHedge',
      isMarketOrder: false,
      instruments: [],
      primaryExchange: hedge.exchange,
      tradeVenue: hedge.account.tradeVenue,
      singleHedge: hedge.params,
    })
    const condition = buildCondition(root, {
      exchange: hedge.exchange,
      tradeVenue: hedge.account.tradeVenue,
      defaultSymbol: hedge.selectedSymbol || hedge.params.hedgeInstrument || '',
      defaultAccountId: accountId,
    })
    await confirmPlan({
      accountId,
      executionBatch: undefined,
      warnings: [],
      triggeredItems: [{ accountId, firePayload, trigger: { condition, expiryMs } }],
      refusals: [],
    })
    // No success toast — the WS algoLifecycle STARTED notification already shows one.
  } catch (error) {
    toast.error(error instanceof Error ? error.message : t('triggerOrderFailed'), {
      position: 'bottom-right',
    })
    play('error')
  } finally {
    ctx.setIsSubmitting(false)
  }
}
